/* eslint no-param-reassign: ["error", { "props": false }] */
import {saveAs} from 'file-saver';
import {forOwn, isArray, isEmpty, isUndefined} from 'lodash';
import {LIBRARY_NAME} from 'src/js/common/constants';

import ImageUtils from './image_utils';
import LogUtils from './log_utils';

const logger = LogUtils.getLogger('FetcherUtils', LIBRARY_NAME);

const EXPIRED_DATE = 'Thu, 01 Jan 1970 00:00:00 GMT';
const DEFAULT_FILE_NAME = 'UNKNOWN_FILENAME.TXT';

interface ProcessOctetStreamResponse {
  result: Record<string, any> | null;
  filename: string | undefined;
}

interface FileProp<T> {
  filename: string;
  result: Promise<T>;
}

interface FetcherUtilsType {
  HTTP_STATUS_NO_CONTENT: number;
  transformJSONToUrlEncodedParams: (obj?: Record<string, any>) => string;
  prepareFormData: (obj: Record<string, any>) => FormData;
  errorHandler: (response: Record<string, any>, rethrow?: boolean) => void;
  setCookieValue: (cookieName: string, value: string, maxAge?: number, path?: string) => void;
  getCookieValue: (cookieName: string, responseCookies?: any) => string;
  resetCookies: (names?: string[], fullPaths?: string[]) => void;
  getResponseHeaders: (response: any) => any;
  parseContentDispositionFilename: (headerValue: string) => string | undefined;
  processJsonResponse: (response: Response) => any;
  processJsonResponseWithTimestamp: (response: Response) => any;
  processTextResponse: (response: Response) => any;
  processEmptyContentResponse: (response: Response) => any;
  processOctetStreamResponse: (
    response: Record<string, any>,
    checkNoContent?: boolean,
    checkContentDisposition?: boolean,
    fileName?: string
  ) => ProcessOctetStreamResponse;
  saveBlobResponse: (file: FileProp<any>) => void;
  getBase64Response: (file: FileProp<any> | null) => Promise<string | void | null>;
}

export const FetcherUtils: FetcherUtilsType = {
  HTTP_STATUS_NO_CONTENT: 204,

  transformJSONToUrlEncodedParams(obj = {}) {
    const str: string[] = [];
    forOwn(obj, (value, key) => {
      if (!isUndefined(value)) {
        str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    return str.join('&');
  },

  prepareFormData(obj) {
    const formData = new FormData();
    if (obj) {
      forOwn(obj, (value, key) => {
        if (value && isArray(value)) {
          value.forEach((item) => {
            if (item instanceof File && item.name) {
              formData.append(key, item, item.name);
            } else {
              formData.append(key, item);
            }
          });
        } else {
          formData.append(key, value);
        }
      });
    }
    return formData;
  },

  errorHandler(response, rethrow = true) {
    let responseMessage = '';
    if (response && response.status) {
      responseMessage = `${response.status} ${response.statusText || response.message || ''}`;
    } else if (response && response.message) {
      responseMessage = response.message;
    } else {
      responseMessage = '';
    }
    logger.error(`WARNING: server responded with an error: ${responseMessage}`);
    if (rethrow) {
      throw new Error(`${responseMessage}`);
    }
  },

  setCookieValue(cookieName, value, maxAge = 0, path = '') {
    let newCookie = `${cookieName}=${value}`;
    newCookie += maxAge === 0 ? '' : `;max-age=${maxAge}`;
    newCookie += path === '' ? '' : `;path=${path}`;
    document.cookie = newCookie;
  },

  getCookieValue(cookieName, responseCookies = undefined) {
    let result;
    let cookies = responseCookies;
    let delimiter = ',';
    if (isEmpty(cookies)) {
      // cookies are not in header, try to get it from document
      cookies = document.cookie;
      delimiter = ';';
    }

    if (!isEmpty(cookies) && cookies) {
      const splitCookies = cookies.split(delimiter);
      if (splitCookies) {
        for (let idx = 0, len = splitCookies.length; idx < len; idx++) {
          if (splitCookies[idx].indexOf(cookieName) !== -1) {
            const cookie = splitCookies[idx].split('=');
            if (cookie.length > 1) {
              [, result] = cookie;
              result = result.trim();
            }
            break;
          }
        }
      }
    }

    return result;
  },

  /* eslint-disable no-restricted-globals */
  resetCookies(names = [], fullPaths = []) {
    let currentPathParts: string[] = [];
    if (location && location.pathname) {
      currentPathParts = location.pathname.split('/');
    }
    names.forEach((name) => {
      // delete cookie without path
      document.cookie = `${name}=; expires=${EXPIRED_DATE};`;

      let pathCurrent = ' path=';
      // delete cookies with all location paths
      if (isArray(currentPathParts)) {
        for (let i = 0, len = currentPathParts.length; i < len; i++) {
          pathCurrent += (pathCurrent.substring(-1) === '/' ? '' : '/') + currentPathParts[i];
          document.cookie = `${name}=; expires=${EXPIRED_DATE};${pathCurrent};`;
        }
      }

      // delete cookies with passed fullPaths
      if (isArray(fullPaths)) {
        fullPaths.forEach((path) => {
          document.cookie = `${name}=; expires=${EXPIRED_DATE}; path=${path};`;
        });
      }
    });
  },
  /* eslint-enable no-restricted-globals */

  /* eslint-disable no-restricted-syntax */
  getResponseHeaders(response) {
    const result = {};
    for (const [headerName, headerValue] of response.headers.entries()) {
      result[headerName] = headerValue;
    }
    return result;
  },
  /* eslint-enable no-restricted-syntax */

  parseContentDispositionFilename(headerValue) {
    const FILENAME_TERM = 'filename=';
    const headerParts = headerValue.split(';');
    let filename: string | undefined = '';
    for (let idx = 0, len = headerParts.length; idx < len; idx++) {
      if (headerParts[idx].indexOf(FILENAME_TERM) !== -1) {
        const filenameParts = headerParts[idx].split('=');
        filename = filenameParts.length > 1 ? filenameParts[1] : undefined;
        break;
      }
    }
    return filename;
  },

  processJsonResponse(response) {
    let result;
    if (response.type === 'opaque' || response.ok) {
      result = response.status === FetcherUtils.HTTP_STATUS_NO_CONTENT ? {} : response.json();
    } else {
      FetcherUtils.errorHandler(response);
    }
    return result;
  },

  processJsonResponseWithTimestamp(response) {
    let result;
    if (response.type === 'opaque' || response.ok) {
      if (response.status !== FetcherUtils.HTTP_STATUS_NO_CONTENT) {
        if (response.headers.get('Timestamp')) {
          result = response.json().then((data) => ({
            ...data,
            timestamp: response.headers.get('Timestamp'),
          }));
        } else {
          // should not happen
          result = response.json();
        }
      } else {
        result = {};
      }
    } else {
      FetcherUtils.errorHandler(response);
    }
    return result;
  },

  processTextResponse(response) {
    let result;
    if (response.type === 'opaque' || response.ok) {
      result = response.text();
    } else {
      FetcherUtils.errorHandler(response);
    }
    return result;
  },

  processEmptyContentResponse(response) {
    const result = {};
    if (!response.ok) {
      FetcherUtils.errorHandler(response);
    }
    return result;
  },

  processOctetStreamResponse(response: Record<string, any>, checkNoContent = false, checkContentDisposition = false, fileName = undefined) {
    let result = null;
    let filename = fileName;
    if (response.type === 'opaque' || response.ok) {
      if (!checkNoContent || response.status !== FetcherUtils.HTTP_STATUS_NO_CONTENT) {
        if (checkContentDisposition) {
          filename = FetcherUtils.parseContentDispositionFilename(response.headers.get('Content-Disposition'));
        }
        result = response.arrayBuffer();
      }
    } else {
      FetcherUtils.errorHandler(response);
    }
    return {result, filename};
  },

  saveBlobResponse(file) {
    const filename = file.filename || DEFAULT_FILE_NAME;
    if (file && file.result) {
      file.result
        .then((data) => {
          if (data && data.byteLength > 0) {
            const fileBlob = new Blob([data]);
            // save file
            saveAs(fileBlob, filename);
          }
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    } else {
      throw new Error(`Corrupted file: ${filename}`);
    }
  },

  /* eslint-disable no-else-return */
  getBase64Response(file) {
    const filename = file?.filename || DEFAULT_FILE_NAME;
    if (file && file.result) {
      return file.result
        .then((data) => {
          let resultData: string | null = null;
          if (data && data.byteLength > 0) {
            resultData = ImageUtils.base64ArrayBuffer(data);
          }
          return resultData;
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    } else if (file && file.result === null) {
      return Promise.resolve(null);
    } else {
      throw new Error(`Corrupted file: ${filename}`);
    }
  },
  /* eslint-enable no-else-return */
};
